import { useQuery } from '@apollo/client'
import { useMemo } from 'react'
import { message } from 'antd'
import moment from 'moment-timezone'
import {
    initialRevisionValues,
    checkEmptyValues,
} from '@Orders/OrderDetailsPage/Status/logic'
import {
    INSPECTION_AND_PROD_STATUS,
    PRODUCT_PENDING_STATUS,
    PRODUCT_COMPLETED_STATUS,
    PRODUCT_INPROGRESS_STATUS,
    UPS_CARRIER_TYPE,
    FEDEX_CARRIER_TYPE,
    UPS_URL,
    FEDEX_URL,
    MOLD_DESIGN_IN_PROGRESS,
    MOLD_COMPLETE_TRAILING_IN_PROGRESS,
    MOLD_FABRICATION_IN_PROGRESS,
    DHL_EXPRESS_CARRIER_TYPE,
    DHL_EXPRESS_URL,
} from '@Orders/constants'
import {
    salesOrderLines_salesOrderLines_cursor as CursorType,
    salesOrderLines_salesOrderLines as SalesOrderLinesType,
    salesOrderLines_salesOrderLines_items as ItemsType,
    salesOrderLines_salesOrderLines_parts as PartsType,
} from '@Orders/OrderDetailsPage/Queries/__generated__/salesOrderLines'
import { salesOrder_salesOrder_shipments_items as SalesOrderShipmentsItemType } from '@Orders/OrderDetailsPage//Queries/__generated__/salesOrder'

import orderedItemsStatusQuery from '~/apps/consumer/pages/Orders/OrderDetailsPage/Queries/orderedItemsStatusQuery'
import { ReportState } from '~/__generated__/globalTypes'
import {
    CONFIG_OPTION_EMPTY,
    PROCESS_DISPLAY_IM,
    PROCESS_DISPLAY_IM_SAMPLE,
} from '~/lib/partsConstants'
import colors from '~/styles/_colors.css'

import { OrderedItemsRecordType } from './types'

export const PLACEMENT_PART_VIEWER =
    'ordered-items-part-viewer-order-detail-page'
export const PLACEMENT_ORDERED_ITEMS = 'ordered-items-table'

export const useOrderedItemsDataQuery = (
    orderId: string,
    statusNotIn: string[],
    limit: number,
    offset: number,
    isImMaafcoEnabled: boolean,
    cursor?: CursorType | null,
): {
    data?: {
        salesOrderLines: SalesOrderLinesType
    }
    loading: boolean
    error?: Error
    refetch: Function
} => {
    const variables: {
        orderId: string
        statusNotIn: string[]
        offset: number
        limit: number
        isImMaafcoEnabled: boolean
        cursor?: CursorType
    } = {
        orderId: orderId,
        statusNotIn: statusNotIn,
        limit: limit,
        offset: offset,
        isImMaafcoEnabled,
    }

    if (cursor) {
        variables.cursor = cursor
    }

    return useQuery(orderedItemsStatusQuery, {
        variables: variables,
        fetchPolicy: 'no-cache',
    })
}

export const intialValues = {
    processNames: [],
    quantity: 0,
    orderedQuantity: 0,
    deliveredQuantity: 0,
    processName: '',
    materialName: '',
    twoDdrawing: null,
    processes: [],
    holes: [],
    dfmFeedback: [],
    producedBy: '',
    status: '',
    inspectionStatus: ReportState.completed,
    productionStatus: ReportState.pending,
    revision: initialRevisionValues,
    totalQuantity: 0,
    material: null,
    shipments: [],
    unitPrice: 0,
    moldStatus: null,
    dfmReports: [],
    erpMoldId: '',
    id: '',
    qmrId: '',
    quote: {},
    key: '',
    associatedErpProductToolId: null,
    quoteModelRevision: null,
    associatedParts: null,
}

export const salesOrderLineDataAdapter = (
    SalesOrderLine: ItemsType & PartsType,
): OrderedItemsRecordType => {
    const {
        id,
        producedBy,
        deliveryStatus,
        quantity,
        orderedQuantity,
        deliveredQuantity,
        material,
        revision,
        processName,
        processNames,
        holes,
        dfmFeedback,
        inspectionStatus,
        productionStatus,
        quoteModelRevisionAttachment,
        processes,
        shipmentAndInspection,
        unitPrice,
        moldStatus,
        quoteModelRevision,
        erpMoldId,
        associatedErpProductToolId,
        associatedParts,
    } = SalesOrderLine

    const productionBy = producedBy
        ? moment(producedBy).format('dddd, MMMM DD, YYYY')
        : CONFIG_OPTION_EMPTY

    return {
        key: id,
        materialName: material?.name || CONFIG_OPTION_EMPTY,
        quantity: quantity,
        orderedQuantity: orderedQuantity,
        deliveredQuantity: deliveredQuantity,
        status: deliveryStatus,
        producedBy: productionBy,
        processName,
        processNames: checkEmptyValues(processNames),
        holes: checkEmptyValues(holes),
        dfmFeedback: checkEmptyValues(dfmFeedback),
        revision: revision,
        inspectionStatus,
        productionStatus,
        twoDdrawing: quoteModelRevisionAttachment,
        processes: checkEmptyValues(processes),
        material: material,
        shipments: checkEmptyValues(shipmentAndInspection),
        unitPrice: unitPrice,
        moldStatus: moldStatus,
        dfmReports: quoteModelRevision?.dfmReports || [],
        erpMoldId,
        id: id,
        qmrId: quoteModelRevision?.id,
        quote: quoteModelRevision?.quoteModel?.quote,
        associatedErpProductToolId: associatedErpProductToolId,
        quoteModelRevision,
        associatedParts,
    }
}

export const useSalesOrderLineData = ({
    salesOrderLines,
}: {
    salesOrderLines: (ItemsType | PartsType)[] | undefined
}) => {
    return useMemo(() => {
        if (!salesOrderLines) {
            return []
        }
        return salesOrderLines?.map((salesOrderLine: ItemsType | PartsType) =>
            salesOrderLineDataAdapter(salesOrderLine as ItemsType & PartsType),
        )
    }, [salesOrderLines])
}

export const getStatus = (
    status: string,
): { status: string; color: string; iconType: string } => {
    const statusInfo = {
        status: INSPECTION_AND_PROD_STATUS[status] || status,
        color: colors.blackAlpha45,
        iconType: 'info-circle',
    }

    switch (status) {
        case PRODUCT_COMPLETED_STATUS:
            statusInfo.color = colors.success
            statusInfo.iconType = 'check-circle'
            break
        case PRODUCT_INPROGRESS_STATUS:
            statusInfo.iconType = 'tool'
            break
        case PRODUCT_PENDING_STATUS:
            statusInfo.iconType = 'clock-circle'
            break
    }

    return statusInfo
}

export const getTrackingUrl = ({
    shipmentsData = [],
    shipmentId = '',
}: {
    shipmentsData?: SalesOrderShipmentsItemType[]
    shipmentId: string
}) => {
    if (!shipmentId || !shipmentsData) {
        return
    }

    const shipmentData = shipmentsData.find(shipmentDataItem =>
        shipmentDataItem?.id?.includes(shipmentId),
    )
    const { carrier, trackingCode } = shipmentData || {}
    let trackingCodeUrl

    if (UPS_CARRIER_TYPE === carrier) {
        trackingCodeUrl = `${UPS_URL}${trackingCode}`
    } else if (FEDEX_CARRIER_TYPE === carrier) {
        trackingCodeUrl = `${FEDEX_URL}${trackingCode}`
    } else if (DHL_EXPRESS_CARRIER_TYPE === carrier) {
        trackingCodeUrl = `${DHL_EXPRESS_URL}${trackingCode}`
    }

    return trackingCodeUrl
}

const PROCESSING_ALERT_CONTENT =
    'Your order is being processed. Some information may appear missing until processing is complete. This may take a few minutes.'
const PROCESSING_ALERT_KEY = 'PROCESSING_ALERT_KEY'
const PROCESSING_ALERT_DURATION = 6

export const showAlertIfProcessingLineItems = (
    lineItems?: SalesOrderLinesType,
) => {
    if (!lineItems) {
        return
    }

    const atLeastOneLineItemStillProcessing = lineItems.items.some(
        lineItem => !lineItem.processName,
    )

    if (atLeastOneLineItemStillProcessing) {
        message.info({
            content: PROCESSING_ALERT_CONTENT,
            key: PROCESSING_ALERT_KEY,
            duration: PROCESSING_ALERT_DURATION,
            type: 'info',
        })
    }
}

export const getPartStatus = (
    record: OrderedItemsRecordType,
    moldStatus?: string | null,
) => {
    const isPendingSamplePartsStatus =
        record.processName?.toLowerCase() ===
            PROCESS_DISPLAY_IM_SAMPLE.toLowerCase() &&
        moldStatus?.toLowerCase() === MOLD_DESIGN_IN_PROGRESS.toLowerCase()

    const isInProgressSamplePartsStatus =
        record.processName?.toLowerCase() ===
            PROCESS_DISPLAY_IM_SAMPLE.toLowerCase() &&
        moldStatus?.toLowerCase() ===
            MOLD_FABRICATION_IN_PROGRESS.toLowerCase() &&
        record.productionStatus !== PRODUCT_COMPLETED_STATUS

    const isPendingProductionPartsStatus =
        record.processName?.toLowerCase() ===
            PROCESS_DISPLAY_IM.toLowerCase() &&
        (moldStatus?.toLowerCase() === MOLD_DESIGN_IN_PROGRESS.toLowerCase() ||
            moldStatus?.toLowerCase() ===
                MOLD_FABRICATION_IN_PROGRESS.toLowerCase() ||
            moldStatus?.toLowerCase() ===
                MOLD_COMPLETE_TRAILING_IN_PROGRESS.toLowerCase()) &&
        record.productionStatus !== PRODUCT_COMPLETED_STATUS

    let status

    if (isPendingSamplePartsStatus || isPendingProductionPartsStatus) {
        status = PRODUCT_PENDING_STATUS
    } else if (isInProgressSamplePartsStatus) {
        status = PRODUCT_INPROGRESS_STATUS
    } else {
        status = record.productionStatus
    }

    return status
}
